type KioskWindow = Window & {
  Android?: Record<string, (...args: unknown[]) => unknown>;
  android?: { app?: { exit?: () => void; close?: () => void } };
  kioskAPI?: {
    close?: () => void;
    systemExit?: () => void;
    exit?: () => void;
    exitApp?: () => void;
    goHome?: () => void;
    home?: () => void;
    quit?: () => void;
  };
  electronAPI?: { close?: () => void; closeApp?: () => void; quit?: () => void };
  twaAPI?: { close?: () => void };
  chrome?: { app?: { close?: () => void } };
  webkit?: {
    messageHandlers?: Record<
      string,
      { postMessage?: (message: unknown) => void }
    >;
  };
};

type KioskNavigator = Navigator & {
  app?: { exitApp?: () => void; close?: () => void };
  standalone?: boolean;
};

const runMethods = (methods: Array<() => void>, delayMs = 80) => {
  methods.forEach((method, index) => {
    window.setTimeout(() => {
      try {
        method();
      } catch {
        /* try the next supported kiosk bridge */
      }
    }, index * delayMs);
  });
};

/** Clear SW caches so the next kiosk launch picks up a fresh build. */
async function clearRuntimeCaches() {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    /* ignore */
  }
  try {
    const regs = await navigator.serviceWorker?.getRegistrations?.();
    if (regs) {
      await Promise.all(regs.map((reg) => reg.unregister()));
    }
  } catch {
    /* ignore */
  }
}

/**
 * Close / exit the kiosk shell (Android WebView, kioskAPI, Electron, PWA).
 * Always tries kiosk bridges — not only when UA says Android.
 */
export function closeApp() {
  const win = window as KioskWindow;
  const nav = navigator as KioskNavigator;

  void clearRuntimeCaches();

  runMethods([
    // Dedicated kiosk shell bridges (Android / Windows / custom WebView)
    () => win.kioskAPI?.close?.(),
    () => win.kioskAPI?.systemExit?.(),
    () => win.kioskAPI?.exitApp?.(),
    () => win.kioskAPI?.exit?.(),
    () => win.kioskAPI?.quit?.(),
    () => win.kioskAPI?.goHome?.(),
    () => win.kioskAPI?.home?.(),

    // Android JavascriptInterface
    () => win.Android?.exitKiosk?.(),
    () => win.Android?.finish?.(),
    () => win.Android?.finishAffinity?.(),
    () => win.Android?.exitApp?.(),
    () => win.Android?.finishActivity?.(),
    () => win.Android?.finishAndRemoveTask?.(),
    () => win.Android?.closeApp?.(),
    () => win.Android?.homeButton?.(),
    () => win.Android?.minimizeApp?.(),
    () => win.Android?.moveTaskToBack?.(true),
    () => win.Android?.goHome?.(),
    () => win.Android?.closeCustomTab?.(),

    // Cordova / legacy
    () => nav.app?.exitApp?.(),
    () => nav.app?.close?.(),
    () => win.android?.app?.exit?.(),
    () => win.android?.app?.close?.(),

    // Electron / desktop kiosk
    () => win.electronAPI?.close?.(),
    () => win.electronAPI?.closeApp?.(),
    () => win.electronAPI?.quit?.(),

    // TWA / Chrome / WKWebView
    () => win.twaAPI?.close?.(),
    () => win.chrome?.app?.close?.(),
    () =>
      win.webkit?.messageHandlers?.closeApp?.postMessage?.({ action: "close" }),
    () =>
      win.webkit?.messageHandlers?.exitApp?.postMessage?.({ action: "exit" }),
    () =>
      win.webkit?.messageHandlers?.kioskClose?.postMessage?.({
        action: "close",
      }),

    () => window.close(),
  ]);

  // Last resort: blank the page so the session is clearly ended
  window.setTimeout(() => {
    try {
      window.location.replace("about:blank");
    } catch {
      /* ignore */
    }
  }, 1800);
}
